/**
 * ImportMedicalRecordsScreen — ★★★ NOT THE KEEPER. TEST 1.04, decided 2026-09-21.
 *
 * THE FACT. Medical-record import exists TWICE in this app:
 *   (1) src/components/MedicalDocumentUpload.jsx, 345 lines — the modal behind the profile
 *       screen's "Auto-fill" link. The one a patient can reach.
 *   (2) THIS FILE, 747 lines at /profile/import-records — its own upload, its own
 *       extraction handling and, until v242.197.0, ITS OWN PROFILE-WRITE PATH.
 * Outside App.jsx's route table, the string 'import-records' appears exactly ONCE in the
 * whole src tree: OrellaChatAgent's intent map (`profile_import_records`). No button, no
 * menu item, no navigate(). It is reachable only if the agent routes here or someone types
 * the URL — and it WRITES THE PROFILE, which is what makes it a defect rather than dead
 * code: a repair that lands in the modal does not land here.
 *
 * ★★★ THE KEEPER IS THE MODAL, AND THE REASON IS THE WRITE PATH, NOT THE LINE COUNT.
 *   The modal SEPARATES extraction from writing: it extracts, lets the patient tick the
 *   items they want, and hands the chosen subset to its owner through onDataExtracted —
 *   so ProfileScreen owns the one write. This screen imports profileAPI and the store
 *   setters and writes the profile itself, which is a second write path BY CONSTRUCTION.
 *   TEST 1.03 is exactly what that costs: the object-shaped `conditions` bug lived in the
 *   modal's owner, and this screen had its own, differently-wrong, shape for the same
 *   field. The modal also offers per-item selection, which this screen does not — it saves
 *   everything it extracted.
 *
 * ★★ WHAT THIS SCREEN HAS AND THE MODAL DOES NOT, so it is not lost when this is settled:
 *   a dedicated camera input (cameraInputRef) and a wider accept list
 *   (.heic/.heif/application/pdf). Those are features to port INTO the modal.
 *
 * ★★★ WHAT IS NOT DECIDED HERE, AND IS NOT ANYONE'S TO DECIDE BUT ALEX'S:
 *   nothing is deleted, and this screen is NOT made unreachable. Removing the route or the
 *   chat-agent intent changes what the app does and is a release. Until he says so, the
 *   invariant this file must hold is the one below: it may not carry a SECOND way of
 *   writing `conditions`. As of v242.197.0 it writes through the shared merge in
 *   src/utils/profileConditions.js, the same one ProfileScreen uses.
 *
 * THE SMALLER HALF OF THE SAME ROW, ALSO RECORDED AND NOT CHANGED: the door that does
 * exist is labelled "Auto-fill" with a sparkle icon. The words "import", "document" and
 * "records" appear nowhere in the UI, which is why the owner of the product could not find
 * his own feature. Renaming it is patient-facing copy across eleven locales — his call.
 */
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Upload, FileText, Camera, Image, Check, X, AlertCircle, Pill, AlertTriangle, Heart, Users, Wine, ChevronDown, ChevronUp, Edit2, Trash2, Plus, Sparkles } from 'lucide-react'
import { useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { profileAPI } from '../../services/api'
// ★★ TEST 1.03 + TEST 1.04 — THE SECOND WRITE PATH. This screen is not linked from any
//   button, but it IS routed (/profile/import-records) and the chat agent can send a
//   patient here, and it writes the profile itself. It did not crash the app — it wrote
//   an array of BARE STRINGS for conditions and {condition, relation} pairs for family
//   history, neither of which is the {id, name} shape every reader indexes — so its
//   imports rendered as nothing. ONE writer for `conditions`, shared with ProfileScreen.
import { toConditionList, mergeImportedConditions, mergeImportedFamilyConditions } from '../../utils/profileConditions'
import { mergeHabitPairs } from '../../stores/useOnboardingStore'
import { fitsGatewayAsDataUrlJson } from '../../utils/uploadLimit'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// R30 · PROD-428 (g): the profile's habit pairs (status, details).
const PROFILE_HABIT_UNITS = [['tobacco', ['tobaccoDetails']], ['alcohol', ['alcoholDetails']], ['drugs', ['drugDetails']]]

export default function ImportMedicalRecordsScreen() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)
  
  const profile = useProfileStore((state) => state.profile)
  const setAllergies = useProfileStore((state) => state.setAllergies)
  const setMedications = useProfileStore((state) => state.setMedications)
  const setMedicalHistory = useProfileStore((state) => state.setMedicalHistory)
  const setFamilyHistory = useProfileStore((state) => state.setFamilyHistory)
  const setSocialLifestyle = useProfileStore((state) => state.setSocialLifestyle)
  
  const [step, setStep] = useState('upload') // upload, processing, review, success
  const [selectedFile, setSelectedFile] = useState(null)
  const [filePreview, setFilePreview] = useState(null)
  const [extractedData, setExtractedData] = useState(null)
  const [error, setError] = useState('')
  const [processing, setProcessing] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    allergies: true,
    medications: true,
    medicalHistory: true,
    familyHistory: true,
    socialLifestyle: true
  })
  const [saving, setSaving] = useState(false)
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif']
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|heic|heif)$/i)) {
        setError(t('errors2.pleaseUploadPdf'))
        return
      }
      
      // Validate file size (max 4 MB)
      if (file.size > 4 * 1024 * 1024) {
        setError(t('errors2.fileSizeLimit'))
        return
      }
      // ★ PROD-228: the request carries the file as base64 (4/3 of it), so a file under the
      //   size above can still be over Lambda's 6 MB request quota (uploadLimit.js); AWS
      //   refuses that before orella-api runs, with no CORS, and it reads as a network error.
      if (!fitsGatewayAsDataUrlJson(file)) {
        setError(t('detect.uploadFailedTooLarge', { name: file.name, defaultValue: '"{{name}}" is too large to upload.' }))
        return
      }
      
      setSelectedFile(file)
      setError('')
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setFilePreview(reader.result)
        }
        reader.readAsDataURL(file)
      } else {
        setFilePreview(null)
      }
    }
  }
  
  const handleProcessDocument = async () => {
    if (!selectedFile) return
    
    setProcessing(true)
    setStep('processing')
    setError('')
    
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result.split(',')[1])
        reader.onerror = reject
        reader.readAsDataURL(selectedFile)
      })
      
      // Send to API for parsing
      const result = await profileAPI.parseMedicalDocument({
        file: base64,
        fileType: selectedFile.type,
        fileName: selectedFile.name
      })
      
      if (result.success && result.data) {
        setExtractedData(result.data)
        setStep('review')
      } else {
        throw new Error(result.error || 'Failed to parse document')
      }
    } catch (err) {
      console.error('Failed to process document:', err)
      setError(err.message || t('errors2.failedToProcessDoc'))
      setStep('upload')
    }
    
    setProcessing(false)
  }
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }
  
  const removeItem = (section, index) => {
    setExtractedData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }))
  }
  
  const handleSaveToProfile = async () => {
    if (!extractedData) return
    
    setSaving(true)
    
    try {
      // Save allergies
      if (extractedData.allergies && extractedData.allergies.length > 0) {
        const existingAllergies = profile?.allergies?.list || []
        const newAllergies = extractedData.allergies.map(a => ({
          allergen: a.substance || a.allergen || a,
          reaction: a.reaction || 'Unknown reaction'
        }))
        
        setAllergies({
          hasAllergies: true,
          list: [...existingAllergies, ...newAllergies],
          completed: true
        })
      }
      
      // Save medications
      if (extractedData.medications && extractedData.medications.length > 0) {
        const existingMeds = profile?.medications?.list || []
        const newMeds = extractedData.medications.map(m => ({
          name: m.name || m,
          dosage: m.dosage || '',
          frequency: m.frequency || ''
        }))
        
        setMedications({
          takesMedications: true,
          list: [...existingMeds, ...newMeds],
          completed: true
        })
      }
      
      // Save medical history
      if (extractedData.medicalHistory && extractedData.medicalHistory.length > 0) {
        const existingConditions = toConditionList(profile?.medicalHistory?.conditions)
        const existingSurgeries = Array.isArray(profile?.medicalHistory?.surgeries) ? profile.medicalHistory.surgeries : []
        
        const conditions = mergeImportedConditions(
          existingConditions,
          extractedData.medicalHistory.filter(h => h.type === 'condition' || !h.type))
        
        const surgeries = extractedData.medicalHistory
          .filter(h => h.type === 'surgery')
          .map(h => ({
            name: h.name || h.procedure || h,
            year: h.year || ''
          }))
        
        setMedicalHistory({
          hasConditions: conditions.length > 0,
          conditions,
          hasSurgeries: surgeries.length > 0 || existingSurgeries.length > 0,
          surgeries: [...existingSurgeries, ...surgeries],
          completed: true
        })
      }
      
      // Save family history
      if (extractedData.familyHistory && extractedData.familyHistory.length > 0) {
        const mergedFamily = mergeImportedFamilyConditions(
          profile?.familyHistory?.conditions,
          profile?.familyHistory?.otherConditions,
          extractedData.familyHistory.map(f => ({
            condition: f.condition || f,
            relation: f.relation || 'Family member'
          })))
        
        setFamilyHistory({
          hasHistory: true,
          ...mergedFamily,
          completed: true
        })
      }
      
      // Save social/lifestyle
      // ★★ R30 · PROD-428 (g), patient P2-2: per habit, the record being imported now (the later
      //   upload; no stored value here carries a document date) supplies status AND details as
      //   one pair. Field by field it printed an old "1 pack daily" beside a new "never". The
      //   same rule as the onboarding auto-fill (useOnboardingStore mergeHabitPairs).
      if (extractedData.socialHistory) {
        const social = extractedData.socialHistory
        const prevSocial = profile?.socialLifestyle || {}
        const { merged } = mergeHabitPairs(prevSocial, social, PROFILE_HABIT_UNITS)
        setSocialLifestyle({
          tobacco: merged.tobacco || 'never',
          tobaccoDetails: merged.tobaccoDetails || '',
          alcohol: merged.alcohol || 'none',
          alcoholDetails: merged.alcoholDetails || '',
          drugs: merged.drugs || 'no',
          drugDetails: merged.drugDetails || '',
          exercise: social.exercise || prevSocial.exercise || '',
          occupation: social.occupation || prevSocial.occupation || '',
          completed: true
        })
      }
      
      setStep('success')
    } catch (err) {
      console.error('Failed to save to profile:', err)
      setError(t('errors2.failedToSaveData'))
    }
    
    setSaving(false)
  }
  
  // ==================== UPLOAD STEP ====================
  if (step === 'upload') {
    return (
      <div className="screen-min-h bg-gradient-to-b from-orella-50 to-white flex flex-col">
        {/* Header */}
        <div className="px-4 pt-4 pb-2">
          <button 
            onClick={() => navigate('/profile')}
            className="p-2 -ml-2 text-gray-600"
          >
            <ArrowLeft size={24} />
          </button>
        </div>
        
        <div className="flex-1 px-4 pb-8">
          {/* Title Section */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orella-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="text-orella-600" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-orella-700 mb-2">
              {t('importRecords.title', 'Import Medical Records')}
            </h1>
            <p className="text-gray-600">
              {t('importRecords.subtitle', "Upload a medical document and we'll automatically extract your health information")}
            </p>
          </div>
          
          {/* Supported Documents */}
          <div className="bg-white rounded-2xl p-4 mb-6 border border-gray-100">
            <h3 className="font-semibold text-gray-800 mb-3">{t('importRecords.worksBestWith')}</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <Check size={16} className="text-green-500" />
                {t('importRecords.typeAvs', 'After Visit Summaries')}
              </li>
              <li className="flex items-center gap-2">
                <Check size={16} className="text-green-500" />
                {t('importRecords.typeDischarge', 'Discharge Summaries')}
              </li>
              <li className="flex items-center gap-2">
                <Check size={16} className="text-green-500" />
                {t('importRecords.typeMedications', 'Medication Lists')}
              </li>
              <li className="flex items-center gap-2">
                <Check size={16} className="text-green-500" />
                {t('importRecords.typeClinicNotes', 'Clinic Notes')}
              </li>
              <li className="flex items-center gap-2">
                <Check size={16} className="text-green-500" />
                {t('importRecords.typeHealthSummaries', 'Health Summaries')}
              </li>
            </ul>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          
          {/* File Selected Preview */}
          {selectedFile && (
            <div className="bg-orella-50 rounded-2xl p-4 mb-6 border border-orella-200">
              <div className="flex items-center gap-3">
                {filePreview ? (
                  <img src={filePreview} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                ) : (
                  <div className="w-16 h-16 bg-orella-100 rounded-lg flex items-center justify-center">
                    <FileText className="text-orella-600" size={24} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-orella-700 truncate">{selectedFile.name}</p>
                  <p className="text-sm text-orella-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedFile(null)
                    setFilePreview(null)
                  }}
                  className="p-2 text-gray-400 hover:text-red-500"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          )}
          
          {/* Upload Options */}
          {!selectedFile && (
            <div className="space-y-3 mb-6">
              {/* File Upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,image/*,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-white border-2 border-dashed border-orella-300 rounded-2xl p-8 flex flex-col items-center gap-3 hover:border-orella-500 hover:bg-orella-50 transition-all"
              >
                <div className="w-14 h-14 bg-orella-100 rounded-full flex items-center justify-center">
                  <Upload className="text-orella-600" size={28} />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-orella-700">{t('importRecords.uploadDocument')}</p>
                  <p className="text-sm text-gray-500">{t('importRecords.pdfOrImageMax10mb')}</p>
                </div>
              </button>
              
              {/* Camera Option */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="w-full bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-4 hover:border-orella-300 transition-all"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <Camera className="text-gray-600" size={24} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-700">{t('importRecords.takeAPhoto')}</p>
                  <p className="text-sm text-gray-500">{t('importRecords.captureYourDocument')}</p>
                </div>
              </button>
            </div>
          )}
          
          {/* Process Button */}
          {selectedFile && (
            <button
              onClick={handleProcessDocument}
              className="w-full bg-orella-500 text-white py-4 rounded-2xl font-semibold hover:bg-orella-600 transition-colors flex items-center justify-center gap-2"
            >
              <Sparkles size={20} />
              {t('importRecords.extractInformation', 'Extract Information')}
            </button>
          )}
          
          {/* Privacy Note */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              🔒 Your document is processed securely and never stored permanently
            </p>
          </div>
        </div>
      </div>
    )
  }
  
  // ==================== PROCESSING STEP ====================
  if (step === 'processing') {
    return (
      <div className="screen-min-h bg-gradient-to-b from-orella-50 to-white flex flex-col items-center justify-center p-8">
        <div className="w-20 h-20 bg-orella-100 rounded-full flex items-center justify-center mb-6">
          <OrellaSpinner size={40} />
        </div>
        <h2 className="text-xl font-bold text-orella-700 mb-2 text-center">
          {t('importRecords.analyzingTitle', 'Analyzing Your Document')}
        </h2>
        <p className="text-gray-500 text-center max-w-xs">
          {t('importRecords.analyzingBody', 'Our AI is extracting medical information from your document...')}
        </p>
        <div className="mt-8 space-y-2 text-sm text-gray-400">
          <p className="flex items-center gap-2">
            <OrellaSpinner size={16} tone="inherit" />
            {t('importRecords.readingDocument', 'Reading document...')}
          </p>
        </div>
      </div>
    )
  }
  
  // ==================== REVIEW STEP ====================
  if (step === 'review' && extractedData) {
    const hasAllergies = extractedData.allergies && extractedData.allergies.length > 0
    const hasMedications = extractedData.medications && extractedData.medications.length > 0
    const hasMedicalHistory = extractedData.medicalHistory && extractedData.medicalHistory.length > 0
    const hasFamilyHistory = extractedData.familyHistory && extractedData.familyHistory.length > 0
    const hasSocialHistory = extractedData.socialHistory && Object.keys(extractedData.socialHistory).length > 0
    
    const hasAnyData = hasAllergies || hasMedications || hasMedicalHistory || hasFamilyHistory || hasSocialHistory
    
    return (
      <div className="screen-min-h bg-gradient-to-b from-orella-50 to-white flex flex-col">
        {/* Header */}
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <button 
            onClick={() => setStep('upload')}
            className="p-2 -ml-2 text-gray-600"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-orella-700">{t('importRecords.reviewInformation')}</h1>
          <div className="w-10" />
        </div>
        
        <div className="flex-1 px-4 pb-8 overflow-auto">
          {!hasAnyData ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="text-amber-600" size={32} />
              </div>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                {t('importRecords.noInformationFound')}
              </h2>
              <p className="text-gray-500 mb-6">
                {t('importRecords.couldNotExtract')}
              </p>
              <button
                onClick={() => setStep('upload')}
                className="bg-orella-500 text-white px-6 py-3 rounded-xl font-semibold"
              >
                {t('importRecords.tryAnotherDocument')}
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-600 text-sm mb-4 text-center">
                {t('importRecords.reviewInstructions', 'Review the extracted information below. You can remove any incorrect items before saving.')}
              </p>
              
              {/* Allergies Section */}
              {hasAllergies && (
                <div className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
                  <button
                    onClick={() => toggleSection('allergies')}
                    className="w-full p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                        <AlertTriangle className="text-red-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-800">{t('importRecords.allergies')}</h3>
                        <p className="text-sm text-gray-500">{extractedData.allergies.length} found</p>
                      </div>
                    </div>
                    {expandedSections.allergies ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                  </button>
                  {expandedSections.allergies && (
                    <div className="px-4 pb-4 space-y-2">
                      {extractedData.allergies.map((allergy, i) => (
                        <div key={i} className="bg-red-50 rounded-xl p-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{allergy.substance || allergy.allergen || allergy}</p>
                            {allergy.reaction && <p className="text-sm text-gray-500">{allergy.reaction}</p>}
                          </div>
                          <button onClick={() => removeItem('allergies', i)} className="p-2 text-gray-400 hover:text-red-500">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Medications Section */}
              {hasMedications && (
                <div className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
                  <button
                    onClick={() => toggleSection('medications')}
                    className="w-full p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Pill className="text-blue-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-800">{t('importRecords.medications')}</h3>
                        <p className="text-sm text-gray-500">{extractedData.medications.length} found</p>
                      </div>
                    </div>
                    {expandedSections.medications ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                  </button>
                  {expandedSections.medications && (
                    <div className="px-4 pb-4 space-y-2">
                      {extractedData.medications.map((med, i) => (
                        <div key={i} className="bg-blue-50 rounded-xl p-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{med.name || med}</p>
                            {(med.dosage || med.frequency) && (
                              <p className="text-sm text-gray-500">
                                {[med.dosage, med.frequency].filter(Boolean).join(' • ')}
                              </p>
                            )}
                          </div>
                          <button onClick={() => removeItem('medications', i)} className="p-2 text-gray-400 hover:text-red-500">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Medical History Section */}
              {hasMedicalHistory && (
                <div className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
                  <button
                    onClick={() => toggleSection('medicalHistory')}
                    className="w-full p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Heart className="text-purple-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-800">{t('importRecords.medicalHistory')}</h3>
                        <p className="text-sm text-gray-500">{extractedData.medicalHistory.length} found</p>
                      </div>
                    </div>
                    {expandedSections.medicalHistory ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                  </button>
                  {expandedSections.medicalHistory && (
                    <div className="px-4 pb-4 space-y-2">
                      {extractedData.medicalHistory.map((item, i) => (
                        <div key={i} className="bg-purple-50 rounded-xl p-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{item.condition || item.name || item}</p>
                            {item.type === 'surgery' && <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded">{t('importRecords.surgery')}</span>}
                            {item.year && <p className="text-sm text-gray-500">{item.year}</p>}
                          </div>
                          <button onClick={() => removeItem('medicalHistory', i)} className="p-2 text-gray-400 hover:text-red-500">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Family History Section */}
              {hasFamilyHistory && (
                <div className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
                  <button
                    onClick={() => toggleSection('familyHistory')}
                    className="w-full p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                        <Users className="text-green-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-800">{t('importRecords.familyHistory')}</h3>
                        <p className="text-sm text-gray-500">{extractedData.familyHistory.length} found</p>
                      </div>
                    </div>
                    {expandedSections.familyHistory ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                  </button>
                  {expandedSections.familyHistory && (
                    <div className="px-4 pb-4 space-y-2">
                      {extractedData.familyHistory.map((item, i) => (
                        <div key={i} className="bg-green-50 rounded-xl p-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{item.condition || item}</p>
                            {item.relation && <p className="text-sm text-gray-500">{item.relation}</p>}
                          </div>
                          <button onClick={() => removeItem('familyHistory', i)} className="p-2 text-gray-400 hover:text-red-500">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Social History Section */}
              {hasSocialHistory && (
                <div className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
                  <button
                    onClick={() => toggleSection('socialLifestyle')}
                    className="w-full p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                        <Wine className="text-amber-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-800">{t('importRecords.socialHistory')}</h3>
                        <p className="text-sm text-gray-500">{t('importRecords.lifestyleFactors')}</p>
                      </div>
                    </div>
                    {expandedSections.socialLifestyle ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                  </button>
                  {expandedSections.socialLifestyle && (
                    <div className="px-4 pb-4 space-y-2">
                      {extractedData.socialHistory.tobacco && (
                        <div className="bg-amber-50 rounded-xl p-3">
                          <p className="text-sm text-gray-500">{t('importRecords.tobacco')}</p>
                          <p className="font-medium text-gray-800 capitalize">{extractedData.socialHistory.tobacco}</p>
                        </div>
                      )}
                      {extractedData.socialHistory.alcohol && (
                        <div className="bg-amber-50 rounded-xl p-3">
                          <p className="text-sm text-gray-500">{t('importRecords.alcohol')}</p>
                          <p className="font-medium text-gray-800 capitalize">{extractedData.socialHistory.alcohol}</p>
                        </div>
                      )}
                      {extractedData.socialHistory.occupation && (
                        <div className="bg-amber-50 rounded-xl p-3">
                          <p className="text-sm text-gray-500">{t('importRecords.occupation')}</p>
                          <p className="font-medium text-gray-800">{extractedData.socialHistory.occupation}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
                  <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              
              {/* Save Button */}
              <button
                onClick={handleSaveToProfile}
                disabled={saving}
                className="w-full bg-orella-500 text-white py-4 rounded-2xl font-semibold hover:bg-orella-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <OrellaSpinner size={20} tone="inherit" />
                    {t('common.saving', 'Saving...')}
                  </>
                ) : (
                  <>
                    <Check size={20} />
                    {t('importRecords.saveToProfile', 'Save to Profile')}
                  </>
                )}
              </button>
              
              <button
                onClick={() => setStep('upload')}
                className="w-full mt-3 py-3 text-gray-600 font-medium"
              >
                {t('importRecords.uploadDifferentDocument')}
              </button>
            </>
          )}
        </div>
      </div>
    )
  }
  
  // ==================== SUCCESS STEP ====================
  if (step === 'success') {
    return (
      <div className="screen-min-h bg-gradient-to-b from-green-50 to-white flex flex-col items-center justify-center p-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <Check className="text-green-600" size={40} />
        </div>
        <h2 className="text-2xl font-bold text-green-700 mb-2 text-center">
          {t('importRecords.profileUpdated', 'Profile Updated!')}
        </h2>
        <p className="text-gray-600 text-center max-w-xs mb-8">
          {t('importRecords.profileUpdatedBody', 'Your medical information has been added to your profile.')}
        </p>
        
        <button
          onClick={() => navigate('/profile')}
          className="w-full max-w-xs bg-orella-500 text-white py-4 rounded-2xl font-semibold hover:bg-orella-600 transition-colors"
        >
          {t('importRecords.backToProfile')}
        </button>
        
        <button
          onClick={() => {
            setStep('upload')
            setSelectedFile(null)
            setFilePreview(null)
            setExtractedData(null)
          }}
          className="mt-4 text-orella-600 font-medium"
        >
          {t('importRecords.importAnotherDocument')}
        </button>
      </div>
    )
  }
  
  return null
}
